import MuiDialog from '@mui/material/Dialog';
import MuiDialogActions from '@mui/material/DialogActions';
import MuiDialogContent from '@mui/material/DialogContent';
import MuiDialogTitle from '@mui/material/DialogTitle';
import MuiDivider from '@mui/material/Divider';
import { useId, type ReactNode } from 'react';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  // Required: a role="dialog" with aria-modal and no accessible name is a defect assistive
  // tech cannot work around, and neither step of the search dialog has a heading to point at.
  label: string;
  // For a caller that DOES render a real heading. Per ARIA it wins over `label`, which stays as
  // the fallback — hence `label` being required rather than one of a pair nobody has to satisfy.
  labelledBy?: string;
  // «The modal is really gone and the page is addressable again» — a different moment from
  // `onClose`, and the one a caller needs when closing makes it change something OUTSIDE the
  // dialog. MUI marks every sibling of the modal `aria-hidden` (the whole app root) and lifts it
  // only when the exit transition ends, so anything written in between is written where assistive
  // tech is not listening, and anything MOUNTED there is born hidden.
  onExited?: () => void;
  // 'sm' by default because the frames are ~600px wide (DESIGN.md §7). Narrower than MUI's full
  // scale on purpose: a panel modal that needs 'lg' is a screen, not a dialog.
  maxWidth?: 'xs' | 'sm' | 'md';
  // Keeps a stray click outside from discarding a form the agent has been filling in. Escape is
  // deliberately still honoured — it is deliberate, a misplaced click is not.
  disableBackdropClose?: boolean;
  // Header and footer slots rather than a component family: neither step of the search dialog has
  // a plain title (one is a search field, the other pin + address + a link), so slots fit where a
  // DialogTitle would not. Same shape as EmptyState.
  title?: ReactNode;
  // A full-bleed rule between the header slot and the body, for a header that reads as a CONTROL
  // rather than as a caption — the coincidences step's search field. A sibling of both slots, so
  // it spans the paper instead of stopping at the header's padding, and in preference to MUI's
  // `dividers` on the content, which draws a second rule under a body that has no footer to be
  // separated from. Ignored without a `title`: a rule with nothing above it separates nothing.
  titleDivider?: boolean;
  actions?: ReactNode;
  children: ReactNode;
}

export function Dialog({
  open,
  onClose,
  label,
  labelledBy,
  onExited,
  maxWidth = 'sm',
  disableBackdropClose = false,
  title,
  titleDivider = false,
  actions,
  children,
}: DialogProps) {
  // The title slot's own id, so it is NOT the one MUI puts in `aria-labelledby`. MUI 6's Dialog
  // always emits that attribute — a generated id when the caller passes none — and DialogTitle
  // claims it off the Dialog's context, which would name the dialog after its header: for the
  // coincidences step that header is a search field, so the name would become whatever the agent
  // typed, overriding the `label` this wrapper makes required. With an id of its own the generated
  // reference resolves to nothing, `label` stays the name, and a caller-supplied `labelledBy` keeps
  // pointing at the caller's own heading instead of being duplicated onto this node.
  const titleId = useId();

  return (
    <MuiDialog
      open={open}
      maxWidth={maxWidth}
      // `maxWidth` only CAPS the paper — without this MUI lets it shrink-wrap its content, so the
      // modal was as wide as whatever it happened to hold: the filters step took its width from the
      // address in its header, the coincidences step from the longest match, and one flow stood at
      // two widths. The frames draw a single column, so the paper claims the width it is capped at.
      fullWidth
      // The name has to land on the PAPER: that is the node MUI gives role="dialog", while
      // anything unrecognized on MuiDialog is spread onto the modal root, which has no role — so
      // a bare `aria-label` here would name nothing and leave the dialog anonymous. MUI 6's
      // Dialog exposes no slots/slotProps at all, so PaperProps is the only route.
      // `borderRadius: 2` — 8px, the radius every other surface in the product is drawn with (Alert,
      // Disclosure, ActionListItem, the tab strip). MUI's default is 4px, which left the one surface
      // that sits on top of all of them the sharpest-cornered thing on screen.
      // `overflowX: 'hidden'` — vertical scroll only, here and on the body below. MUI's
      // `overflow-y: auto` makes both scroll sideways the moment anything pokes past them, and a
      // writing-assistant extension (Grammarly, LanguageTool) overlays a text field with a box a
      // little wider than it. That box can hang off the paper itself, out of the body's reach, so
      // the paper clips too. Nothing a dialog holds is meant to be wider than the dialog.
      PaperProps={{ 'aria-label': label, sx: { borderRadius: 2, overflowX: 'hidden' } }}
      // MUI's own prop, which it puts on that same paper — so both names sit on the node holding
      // the role and ARIA's precedence (labelledby over label) applies without the wrapper
      // choosing. Conditional spread: `exactOptionalPropertyTypes` rejects an explicit undefined.
      {...(labelledBy !== undefined ? { 'aria-labelledby': labelledBy } : {})}
      // TransitionProps, NEVER Modal's `onTransitionExited` — which reads like the obvious hook
      // and fires on the wrong side of the un-hide. MUI chains the child transition's own
      // onExited AFTER its internal handler, and that handler is what removes the modal from the
      // manager and strips `aria-hidden` off the siblings. Wire it to the other prop and every
      // caller silently gets the defect this slot exists to remove.
      {...(onExited !== undefined ? { TransitionProps: { onExited } } : {})}
      // MUI reports (event, reason); the façade exposes neither. The reason is consumed here so
      // no caller has to learn MUI's vocabulary to protect a filled form.
      onClose={(_event, reason) => {
        if (disableBackdropClose && reason === 'backdropClick') return;
        onClose();
      }}
    >
      {/* `component="div"`: MUI's DialogTitle is an <h2>, and what goes in this slot is a search
          field or an address beside a link — neither is a heading, and announcing one as such
          would be worse than the styling is worth. The accessible name comes from `label`. */}
      {title !== undefined && (
        <MuiDialogTitle component="div" id={titleId}>
          {title}
        </MuiDialogTitle>
      )}
      {title !== undefined && titleDivider && <MuiDivider />}
      {/* Vertical scroll only, like the paper: see the note on PaperProps above. */}
      <MuiDialogContent sx={{ overflowX: 'hidden' }}>{children}</MuiDialogContent>
      {/* Inset to the BODY's column (24px), not to MUI's own 8px: the footer's buttons are the last
          row of the same form, and at 8px they overhang every field and title above them. The bottom
          takes the same value, since a footer flush to the paper's edge reads as content that ran
          out of room. Aligned here rather than by each caller — the alignment is the wrapper's job,
          and a slice cannot reach MUI to fix it. */}
      {actions !== undefined && (
        <MuiDialogActions sx={{ px: 3, pb: 3 }}>{actions}</MuiDialogActions>
      )}
    </MuiDialog>
  );
}
